import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'

/**
 * Settings → Easeedesk → Recommended actions.
 *
 * Perechile vechi: `settings/easeedesk/chat_recommended_actions`,
 * `chatRecommendedActionsList`, `getChatRecommendedAction`,
 * `approveChatAction`, `rejectChatAction`, `removeChatAction`, `quick_action`.
 *
 * `apiGet` / `apiPost` pun ELE `/api/v2` in fata caii.
 */

const BASE = '/settings/easeedesk/chat_recommended_actions'

/** `status` din `it_easeedesk_chat_recommended_actions`. */
export const PENDING = 0
export const APPROVED = 1
export const REJECTED = 2

export interface ChatActionRow {
  ID: number
  search_value: string
  status: number
  /** null = actiune comuna, a nimanui anume ("General" in casuta). */
  service_provider: number | null
  service_provider_name: string | null
  /** null = toate firmele. */
  company_id: number | null
  company_name: string | null
  /** Cine a propus-o; gol = a intrat de la sine. */
  user_name: string
  messages: string[]
  commands: string[]
}

export interface ChatActionsTablePage {
  draw: number
  recordsTotal: number
  recordsFiltered: number
  data: ChatActionRow[]
}

export interface ChatActionsShell {
  title: string
  moduleUrl: string
  companies: { ID: number; name: string }[]
  service_provider: number
  service_provider_name: string
}

export interface ChatActionDetails {
  ID: number
  service_provider: number | null
  company_id: number | null
  search_value: string
  status: number
  user_id: number
}

export interface ChatActionCommand {
  id: number
  display_name: string
  command: string
  has_parameters: number
  info: string
  selected: boolean
}

export interface ChatActionPage {
  details: ChatActionDetails
  messages: { ID: number; message: string; status: number }[]
  commands: ChatActionCommand[]
}

export interface ChatActionsTableRequest {
  draw: number
  start: number
  length: number
  orderColumn: number
  orderName: string
  orderDir: 'asc' | 'desc'
  search: string
}

/** Aceiasi parametri pe care ii trimitea DataTables pe `chatRecommendedActionsList`. */
export function chatActionsTableParams(r: ChatActionsTableRequest): URLSearchParams {
  const p = new URLSearchParams()
  p.set('draw', String(r.draw))
  p.set('start', String(r.start))
  p.set('length', String(r.length))
  p.set('order[0][column]', String(r.orderColumn))
  p.set('order[0][dir]', r.orderDir)
  p.set(`columns[${r.orderColumn}][name]`, r.orderName)
  p.set('search[value]', r.search)
  return p
}

export function chatActionsShellQuery() {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_recommended_actions'],
    queryFn: () => apiGet<ChatActionsShell>(BASE),
  })
}

export function chatActionsTableQuery(r: ChatActionsTableRequest) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_recommended_actions', 'table', r],
    queryFn: () =>
      apiGet<ChatActionsTablePage>(
        `${BASE}/chatRecommendedActionsList?${chatActionsTableParams(r).toString()}`,
      ),
  })
}

export function chatActionQuery(action: number) {
  return queryOptions({
    queryKey: ['settings', 'easeedesk', 'chat_recommended_actions', 'one', action],
    queryFn: () => apiPost<ChatActionPage>(`${BASE}/getChatRecommendedAction`, { action }),
    enabled: action > 0,
  })
}

export interface ChatActionApproval {
  action_id: number
  /** 0 = "General": regula e a tuturor. */
  service_provider: number
  /** 0 = toate firmele. */
  company_id: number
  keywords: string
  messages: string[]
  commands: number[]
}

export function approveChatAction(fields: ChatActionApproval) {
  return apiPost<{ ok?: boolean }>(`${BASE}/approveChatAction`, {
    action_id: fields.action_id,
    service_provider: fields.service_provider,
    company_id: fields.company_id,
    keywords: fields.keywords,
    messages: fields.messages,
    commands: fields.commands,
  })
}

export function rejectChatAction(action: number) {
  return apiPost<{ ok?: boolean }>(`${BASE}/rejectChatAction`, { action })
}

export function removeChatAction(action: number) {
  return apiPost<{ ok?: boolean }>(`${BASE}/removeChatAction`, { action })
}

/** Butoanele scurte din lista. `aprove` e scris asa si in portal. */
export function quickChatAction(type: 'aprove' | 'reject', action: number) {
  return apiGet<{ ok?: boolean; status?: number; message?: string }>(
    `${BASE}/quick_action/${type}/${action}`,
  )
}
